<script setup>
import Chart from 'chart.js/auto'
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { downloadChartAsPNG } from '@/utils/downloadChart.js'
import { getThemeColors, DIMMED_COLOR, createStarPointStyles, restyleStarPoints } from '@/utils/analysisCharts.js'
import { useChartPointerDrag, rafBatch } from '@/utils/chartPointerDrag.js'

/*
  Proper-motion vector-point diagram (pmRA vs pmDec) of the Gaia-matched stars.
  Cluster stars share a common space motion, so they clump together while field stars
  spread out. The membership selection is drawn as a circle overlay (in data units, so
  it renders as an ellipse in pixels) and is directly draggable:
    - drag inside the circle to move its center
    - drag its edge to grow/shrink the radius
    - pinch with two fingers to resize and move it at once
    - drag on an empty plot (no active selection) to create one from its corner
  There are also fields underneath the plot that reflect the value of the pmRA/pmDec and pmRadii.
*/

const props = defineProps({
  cmd: {
    type: Array,
    required: true
  },
  // the membership selection {pmra, pmdec, pm_radius, ...} drawn and edited on the plot
  selection: {
    type: Object,
    default: null
  },
  // per-star member flags aligned with cmd, or null when no selection is active
  memberFlags: {
    type: Array,
    default: null
  },
  // the backend's suggested selection, applied by the Use Defaults button
  membershipGuess: {
    type: Object,
    default: null
  },
  // true if the plot is in the active tab, false if not to prevent updates
  active: {
    type: Boolean,
    default: true
  },
  // target the operation was run on, shown in the title when known
  clusterName: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['update:selection'])

const chartTitle = computed(() => (props.clusterName ? `${props.clusterName}: Proper Motions` : 'Proper Motions'))

const properMotionCanvas = ref(null)
let properMotionChart = null
let themeColors = {}

// clamp the axes to the bulk of the distribution so extreme field stars don't shrink the clump
const BOUND_QUANTILE = 0.05
const BOUND_PADDING_FRACTION = 0.3
const MIN_HALF_RANGE = 5.0
// edge width for resizing by mouse or by touch controls
const EDGE_GRAB_PIXELS = 10
const EDGE_GRAB_PIXELS_TOUCH = 22
// fingers landing this close together give a separation ratio that explodes on the first move
const MIN_PINCH_PIXELS = 20
const MIN_PM_RADIUS = 0.1

// True if this is running on a tablet with touch screen, false otherwise.
const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false

// point objects depend only on the cmd data, never on the membership selection, so
// they are computed only when the underlying cmd data changes
const gaiaPoints = computed(() => {
  const points = []
  props.cmd.forEach((star, index) => {
    if (star.gaia_match && Number.isFinite(star.pmra) && Number.isFinite(star.pmdec)) {
      points.push({
        x: star.pmra,
        y: star.pmdec,
        star: star,
        cmdIndex: index
      })
    }
  })
  return points
})

function quantileBounds(values) {
  const sorted = [...values].sort((a, b) => a - b)
  const lower = sorted[Math.floor(BOUND_QUANTILE * (sorted.length - 1))]
  const upper = sorted[Math.ceil((1 - BOUND_QUANTILE) * (sorted.length - 1))]
  const padding = Math.max((upper - lower) * BOUND_PADDING_FRACTION, MIN_HALF_RANGE - (upper - lower) / 2)
  return { min: lower - padding, max: upper + padding }
}

function axisBounds() {
  const points = gaiaPoints.value
  if (!points.length) return { x: { min: -20, max: 20 }, y: { min: -20, max: 20 } }
  return {
    x: quantileBounds(points.map((point) => point.x)),
    y: quantileBounds(points.map((point) => point.y))
  }
}

function pointStyles() {
  return createStarPointStyles(gaiaPoints.value, { memberFlags: props.memberFlags, themeColors })
}

function buildDataset() {
  const { colors, radii, shapes } = pointStyles()
  return {
    data: gaiaPoints.value,
    parsing: false,
    pointBackgroundColor: colors,
    pointBorderColor: colors,
    pointRadius: radii,
    pointStyle: shapes,
    pointHoverRadius: 5,
    pointHoverBackgroundColor: themeColors.secondary,
    pointHoverBorderColor: themeColors.secondary
  }
}

// the legend is informational (the member/field split is per-point color, not
// per-dataset), so its entries are generated by hand
function legendLabels() {
  const entries = [
    { text: 'Members', fillStyle: themeColors.primary, strokeStyle: themeColors.primary, pointStyle: 'circle', fontColor: themeColors.text },
    { text: 'Field stars', fillStyle: DIMMED_COLOR, strokeStyle: DIMMED_COLOR, pointStyle: 'circle', fontColor: themeColors.text }
  ]
  if (gaiaPoints.value.some((point) => point.star.gaia_only)) {
    entries.push({ text: 'Gaia catalog', fillStyle: themeColors.primary, strokeStyle: themeColors.primary, pointStyle: 'triangle', fontColor: themeColors.text })
  }
  return entries
}

function selectionActive() {
  const selection = props.selection
  return selection && selection.pmra !== null && selection.pmdec !== null
    && selection.pm_radius !== null
}

// the selection circle's center and radii in pixel space (an ellipse, since the scales differ).
function selectionEllipse(chart) {
  const selection = props.selection
  const xScale = chart.scales.x
  const yScale = chart.scales.y
  const centerX = xScale.getPixelForValue(selection.pmra)
  const centerY = yScale.getPixelForValue(selection.pmdec)
  return {
    centerX: centerX,
    centerY: centerY,
    radiusX: Math.abs(xScale.getPixelForValue(selection.pmra + selection.pm_radius) - centerX),
    radiusY: Math.abs(yScale.getPixelForValue(selection.pmdec + selection.pm_radius) - centerY)
  }
}

// draws the pm_radius selection circle
const selectionRegionPlugin = {
  id: 'pmSelectionRegion',
  afterDatasetsDraw(chart) {
    if (!selectionActive()) return
    const { centerX, centerY, radiusX, radiusY } = selectionEllipse(chart)

    const { ctx } = chart
    ctx.save()
    ctx.strokeStyle = themeColors.secondary
    ctx.lineWidth = 2
    ctx.setLineDash([6, 4])
    ctx.beginPath()
    ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI)
    ctx.stroke()
    ctx.restore()
  }
}

/* ---- direct manipulation of the selection circle ---- */

let dragMode = null   // 'move' | 'resize' | 'create' | null
let dragOffset = { x: 0, y: 0 }
let createAnchor = null
let pinchStart = null   // circle + finger geometry captured when the second finger lands

// round emitted selection values to 3 decimals (~milliarcsec)
function round3(value) {
  return Math.round(value * 1000) / 1000
}

const emitSelection = rafBatch((updates) => emit('update:selection', { ...props.selection, ...updates }))

// a pointer position in pixels -> the same position in data units (mas/yr)
function toData(point) {
  return {
    x: properMotionChart.scales.x.getValueForPixel(point.pixelX),
    y: properMotionChart.scales.y.getValueForPixel(point.pixelY)
  }
}

// keep the circle's center inside the plotted axis ranges - pointer capture keeps
// reporting positions past the chart edge, and a circle dragged fully off-plot
// would have nothing left to grab
function clampCenter(x, y) {
  const xScale = properMotionChart.scales.x
  const yScale = properMotionChart.scales.y
  return {
    x: Math.min(Math.max(x, xScale.min), xScale.max),
    y: Math.min(Math.max(y, yScale.min), yScale.max)
  }
}

// the created circle is inscribed in the square box whose near corner is the pointer-down
// anchor, sized by the longer of the two drag directions 
function cornerSelection(anchor, point) {
  const side = Math.max(Math.abs(point.x - anchor.x), Math.abs(point.y - anchor.y))
  const radius = Math.max(side / 2, MIN_PM_RADIUS)
  // Math.sign is 0 before the drag has a direction, which leaves the circle on the anchor
  const center = clampCenter(
    anchor.x + Math.sign(point.x - anchor.x) * radius,
    anchor.y + Math.sign(point.y - anchor.y) * radius
  )
  return { pmra: round3(center.x), pmdec: round3(center.y), pm_radius: round3(radius) }
}

// mouses get a smaller edge grab pixel width than non-mouse touch events
function edgeGrabPixels(event) {
  return event.pointerType === 'mouse' ? EDGE_GRAB_PIXELS : EDGE_GRAB_PIXELS_TOUCH
}

function hitTest(pixelX, pixelY, grabPixels) {
  if (!selectionActive()) return null
  const { centerX, centerY, radiusX, radiusY } = selectionEllipse(properMotionChart)
  if (radiusX <= 0 || radiusY <= 0) return null
  const minRadius = Math.min(radiusX, radiusY)
  // on a small circle a full-width grab band would swallow the whole interior and leave
  // nothing to drag the circle by, so the edge never claims more than the outer half
  const band = Math.min(grabPixels, minRadius / 2)
  // normalized radial coordinate: 1.0 lies exactly on the ellipse boundary
  const rho = Math.hypot((pixelX - centerX) / radiusX, (pixelY - centerY) / radiusY)
  if (Math.abs(rho - 1) * minRadius <= band) return 'edge'
  if (rho < 1) return 'inside'
  return null
}

function onDown(point, event) {
  const at = toData(point)
  const hit = hitTest(point.pixelX, point.pixelY, edgeGrabPixels(event))
  if (hit === 'edge') {
    dragMode = 'resize'
  }
  else if (hit === 'inside') {
    dragMode = 'move'
    dragOffset = { x: at.x - props.selection.pmra, y: at.y - props.selection.pmdec }
    properMotionChart.canvas.style.cursor = 'grabbing'
  }
  else if (!selectionActive()) {
    // dragging on an empty plot creates a selection cornered at the anchor point
    dragMode = 'create'
    createAnchor = clampCenter(at.x, at.y)
    emitSelection(cornerSelection(createAnchor, createAnchor))
  }
  else {
    return false
  }
  return true
}

function onDrag(point) {
  const at = toData(point)
  if (dragMode === 'move') {
    const center = clampCenter(at.x - dragOffset.x, at.y - dragOffset.y)
    emitSelection({ pmra: round3(center.x), pmdec: round3(center.y) })
  }
  else if (dragMode === 'resize') {
    // both axes are mas/yr, so the radius is a plain data-space distance (same as the member predicate)
    const radius = Math.hypot(at.x - props.selection.pmra, at.y - props.selection.pmdec)
    emitSelection({ pm_radius: round3(Math.max(radius, MIN_PM_RADIUS)) })
  }
  else if (dragMode === 'create') {
    emitSelection(cornerSelection(createAnchor, at))
  }
}

// Decide the mouse icon based on what mouse is hovering over
function onHover(point, event) {
  const hit = hitTest(point.pixelX, point.pixelY, edgeGrabPixels(event))
  return hit === 'edge' ? 'ew-resize' : hit === 'inside' ? 'grab' : selectionActive() ? 'default' : 'crosshair'
}

function onUp() {
  dragMode = null
  createAnchor = null
  properMotionChart.canvas.style.cursor = 'default'
}

// Two-finger pinch resizes the circle by the change in finger separation and moves it by the
// change in midpoint, so one gesture does what edge-drag and inside-drag do separately. Both
// are measured in data units.
function fingerGeometry(points) {
  const [first, second] = points.map(toData)
  return {
    separation: Math.hypot(second.x - first.x, second.y - first.y),
    midpoint: { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 }
  }
}

function onPinchStart(points) {
  const pixelSeparation = Math.hypot(points[1].pixelX - points[0].pixelX, points[1].pixelY - points[0].pixelY)
  // Can only pinch if you already have a circle selection created
  if (!selectionActive() || pixelSeparation < MIN_PINCH_PIXELS) {
    pinchStart = null
    return
  }
  pinchStart = {
    ...fingerGeometry(points),
    radius: props.selection.pm_radius,
    center: { x: props.selection.pmra, y: props.selection.pmdec }
  }
}

function onPinchMove(points) {
  if (!pinchStart) return
  const { separation, midpoint } = fingerGeometry(points)
  const center = clampCenter(
    pinchStart.center.x + midpoint.x - pinchStart.midpoint.x,
    pinchStart.center.y + midpoint.y - pinchStart.midpoint.y
  )
  const radius = Math.max(pinchStart.radius * (separation / pinchStart.separation), MIN_PM_RADIUS)
  emitSelection({ pmra: round3(center.x), pmdec: round3(center.y), pm_radius: round3(radius) })
}

function onPinchEnd() {
  pinchStart = null
}

const { attach: attachPointerHandlers, detach: detachPointerHandlers } = useChartPointerDrag({
  chart: () => properMotionChart,
  onDown,
  onDrag,
  onHover,
  onUp,
  onPinchStart,
  onPinchMove,
  onPinchEnd
})

/* ---- numeric controls ---- */

function setField(field, value) {
  emit('update:selection', { ...props.selection, [field]: value })
}

const hasDefaults = computed(() => {
  const guess = props.membershipGuess
  return !!guess && Number.isFinite(guess.pmra) && Number.isFinite(guess.pmdec) && Number.isFinite(guess.pm_radius)
})

function useDefaults() {
  emit('update:selection', {
    ...props.selection,
    pmra: props.membershipGuess.pmra,
    pmdec: props.membershipGuess.pmdec,
    pm_radius: props.membershipGuess.pm_radius
  })
}

function clearSelection() {
  emit('update:selection', {
    ...props.selection,
    pmra: null,
    pmdec: null,
    pm_radius: null
  })
}

/* ---- chart lifecycle ---- */

function tooltipLines(context) {
  const star = context.raw?.star
  if (!star) return []
  const lines = [
    `pm: ${star.pmra}, ${star.pmdec} mas/yr`,
    `parallax: ${star.parallax} mas`,
    star.gaia_only ? `Gaia catalog star (G = ${star.g_mag})` : `mag: ${star.mag}`
  ]
  // Bailer-Jones geometric distance with its 16th-84th percentile range, when the source has one
  if (star.distance != null) {
    lines.push(`distance: ${Math.round(star.distance)} pc (${Math.round(star.distance_lo)}–${Math.round(star.distance_hi)})`)
  }
  return lines
}

// restyle the existing datasets in place by changing their properties depending on the membership group they are in.
// Replacing them would recreate the chart elements and cause flickering in the points on the plot.
function updateChart() {
  const bounds = axisBounds()
  const dataset = properMotionChart.data.datasets[0]
  const { colors, radii } = pointStyles()
  dataset.data = gaiaPoints.value
  dataset.pointBackgroundColor = colors
  dataset.pointBorderColor = colors
  dataset.pointRadius = radii
  properMotionChart.options.scales.x.min = bounds.x.min
  properMotionChart.options.scales.x.max = bounds.x.max
  properMotionChart.options.scales.y.min = bounds.y.min
  properMotionChart.options.scales.y.max = bounds.y.max
  properMotionChart.options.plugins.legend.display = !!props.memberFlags
  properMotionChart.update()
}

function createChart() {
  themeColors = getThemeColors()

  const bounds = axisBounds()

  properMotionChart = new Chart(properMotionCanvas.value, {
    type: 'scatter',
    data: {
      datasets: [buildDataset()]
    },
    plugins: [selectionRegionPlugin],
    options: {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'nearest', intersect: true },
      scales: {
        x: {
          type: 'linear',
          min: bounds.x.min,
          max: bounds.x.max,
          title: { display: true, text: 'pmRA (mas/yr)', color: themeColors.text },
          border: { color: themeColors.text, width: 2 },
          ticks: { color: themeColors.text },
          grid: { color: themeColors.background, tickColor: themeColors.text }
        },
        y: {
          min: bounds.y.min,
          max: bounds.y.max,
          title: { display: true, text: 'pmDec (mas/yr)', color: themeColors.text },
          border: { color: themeColors.text, width: 2 },
          ticks: { color: themeColors.text },
          grid: { color: themeColors.background, tickColor: themeColors.text }
        }
      },
      plugins: {
        legend: {
          display: !!props.memberFlags,
          // informational only: clicking must not hide the single shared dataset
          onClick: () => {},
          labels: { color: themeColors.text, usePointStyle: true, generateLabels: legendLabels }
        },
        tooltip: { callbacks: { label: tooltipLines } }
      }
    }
  })
  lastCmd = props.cmd
  lastLegendShown = !!props.memberFlags
}

// Keep track of changes in the data
let lastCmd = null
let lastLegendShown = null
let refreshPending = false

function refreshChart() {
  // If this tab isn't currently displayed, flag the update for the next time this tab becomes active
  if (!props.active) {
    refreshPending = true
    return
  }
  refreshPending = false
  const legendShown = !!props.memberFlags
  // Checks if the underlying data (cmd) has changed, or legend has changed what is shown/hidden
  // If not, we can just restyle the points in place. 
  if (props.cmd === lastCmd && legendShown === lastLegendShown
      && restyleStarPoints(properMotionChart, pointStyles())) {
    return
  }
  // If so, we need to update the chart, which takes order of magnitude more time
  lastCmd = props.cmd
  lastLegendShown = legendShown
  updateChart()
}

watch(
  () => [props.cmd, props.memberFlags, props.selection?.pmra, props.selection?.pmdec, props.selection?.pm_radius],
  () => {
    if (properMotionChart) {
      refreshChart()
    }
  }
)

// When the tab becomes active and visible again, if we had a pending update, refresh the chart now.
watch(() => props.active, (active) => {
  if (active && refreshPending && properMotionChart) {
    refreshChart()
  }
})

onMounted(() => {
  createChart()
  attachPointerHandlers()
})

onBeforeUnmount(() => {
  detachPointerHandlers()
})

</script>
<template>
  <div class="wrapper">
    <p class="title-pm">
      {{ chartTitle }}
      <v-btn
        icon="mdi-download"
        class="download-btn"
        title="Download as PNG"
        @click="downloadChartAsPNG(properMotionChart, 'proper-motions.png', chartTitle)"
      />
    </p>
    <div class="pm-plot-wrapper">
      <canvas
        ref="properMotionCanvas"
        class="pm-plot"
      />
    </div>
    <p class="drag-hint">
      Drag the circle to move it, {{ coarsePointer ? 'pinch or drag its edge' : 'drag its edge' }} to resize{{ selectionActive() ? '' : ', or drag from one corner of the cluster to select it' }}
    </p>
    <v-sheet
      class="filter-controls pa-3 d-flex flex-wrap align-center ga-3"
      color="var(--card-background)"
      rounded
    >
      <v-number-input
        :model-value="props.selection?.pmra"
        label="pmRA (mas/yr)"
        :precision="null"
        control-variant="hidden"
        density="compact"
        hide-details
        class="filter-input"
        @update:model-value="setField('pmra', $event)"
      />
      <v-number-input
        :model-value="props.selection?.pmdec"
        label="pmDec (mas/yr)"
        :precision="null"
        control-variant="hidden"
        density="compact"
        hide-details
        class="filter-input"
        @update:model-value="setField('pmdec', $event)"
      />
      <v-number-input
        :model-value="props.selection?.pm_radius"
        label="PM radius (mas/yr)"
        :precision="null"
        :min="0"
        control-variant="hidden"
        density="compact"
        hide-details
        class="filter-input"
        @update:model-value="setField('pm_radius', $event)"
      />
      <v-btn
        text="Use Defaults"
        size="small"
        color="var(--primary-interactive)"
        :disabled="!hasDefaults"
        @click="useDefaults"
      />
      <v-btn
        text="Clear"
        size="small"
        color="var(--cancel)"
        @click="clearSelection"
      />
    </v-sheet>
  </div>
</template>

<style scoped>
.wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
}
.title-pm {
  align-self: center;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0 0 0.5rem;
}
.pm-plot-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  min-height: 0;
  width: 100%;
  position: relative;
}
.pm-plot {
  width: 100% !important;
  height: 100% !important;
  touch-action: none;
}
.drag-hint {
  align-self: center;
  font-size: 0.75rem;
  color: var(--info);
  margin: 0.25rem 0 0;
}
.filter-controls {
  flex: 0 0 auto;
  align-self: center;
  width: min(100%, 1120px);
  margin-top: 0.75rem;
  color: var(--text);
}
.filter-input {
  min-width: 9.5rem;
  max-width: 11rem;
}
.download-btn {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
}
</style>
